import { z } from 'zod/v4'

// ============================================
// LLM Provider Env Schema Definition
// ============================================

/**
 * Schema for defining environment variable requirements for a provider.
 * Each entry maps a logical name to its configuration.
 */
export const LlmProviderEnvEntrySchema = z.object({
  /** The environment variable key to look up (e.g., "OPENAI_API_KEY") */
  key: z.string(),
  /** Whether this env var is required */
  required: z.boolean().default(true),
  /** Optional description for UI/docs */
  description: z.string().optional(),
})

export type LlmProviderEnvEntry = z.infer<typeof LlmProviderEnvEntrySchema>

export const LlmProviderEnvSchemaSchema = z.record(
  z.string(),
  LlmProviderEnvEntrySchema,
)

export type LlmProviderEnvSchema = z.infer<typeof LlmProviderEnvSchemaSchema>

/**
 * Parse and validate an envSchema JSON value from the database
 */
export function parseEnvSchema(envSchemaJson: unknown): LlmProviderEnvSchema {
  if (!envSchemaJson || typeof envSchemaJson !== 'object') {
    return {}
  }

  try {
    return LlmProviderEnvSchemaSchema.parse(envSchemaJson)
  } catch {
    // Invalid schema, return empty
    return {}
  }
}

// ============================================
// LLM Provider Schema (matches Prisma LlmProvider model)
// ============================================

export const LlmProviderSchema = z.object({
  id: z.string(),
  slug: z.string(),
  displayName: z.string(),
  baseUrl: z.string().nullable(),
  /** JSON schema defining required environment variables for this provider */
  envSchema: LlmProviderEnvSchemaSchema.optional().default({}),
  system: z.boolean(),
  isActive: z.boolean(),
  workplaceId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type LlmProvider = z.infer<typeof LlmProviderSchema>

// ============================================
// LLM Provider Option (for UI dropdowns)
// ============================================

export const LlmProviderOptionSchema = z.object({
  value: z.string(), // id
  label: z.string(), // displayName
  slug: z.string(),
  system: z.boolean(),
})

export type LlmProviderOption = z.infer<typeof LlmProviderOptionSchema>

// ============================================
// LLM Model Capabilities
// ============================================

export const LlmCapabilitiesSchema = z.object({
  text: z.boolean().default(true),
  image: z.boolean().default(false),
  audio: z.boolean().default(false),
  pdf: z.boolean().default(false),
  reasoning: z.boolean().default(false),
  /** Speech-to-text / transcription vendor — not a chat LanguageModel */
  transcription: z.boolean().default(false),
})

export type LlmCapabilities = z.infer<typeof LlmCapabilitiesSchema>

// ============================================
// LLM Model Schema (matches Prisma LlmModel model)
// ============================================

export const LlmModelSchema = z.object({
  id: z.string(),
  llmProviderId: z.string(),
  /** Model ID in gateway format (e.g., "openai/gpt-4o-mini") */
  modelId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  contextWindow: z.number(),
  inputPricePerMillion: z.number(),
  outputPricePerMillion: z.number(),
  capabilities: LlmCapabilitiesSchema,
  isActive: z.boolean(),
  isDefault: z.boolean(),
  sortOrder: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type LlmModel = z.infer<typeof LlmModelSchema>

// ============================================
// LLM Model with Provider (for joined queries)
// ============================================

export const LlmModelWithProviderSchema = LlmModelSchema.extend({
  llmProvider: LlmProviderSchema,
})

export type LlmModelWithProvider = z.infer<typeof LlmModelWithProviderSchema>

// ============================================
// LLM Model Option (for UI dropdowns)
// ============================================

export const LlmModelOptionSchema = z.object({
  value: z.string(), // modelId (gateway format)
  label: z.string(), // name
  providerSlug: z.string(), // provider slug for grouping
  providerName: z.string(), // provider displayName
  description: z.string().nullable(),
  capabilities: LlmCapabilitiesSchema,
})

export type LlmModelOption = z.infer<typeof LlmModelOptionSchema>

// ============================================
// LLM Usage Schema (matches Prisma model)
// ============================================

export const LlmUsageSchema = z.object({
  id: z.string(),
  llmModelId: z.string(),
  workplaceId: z.string().nullable(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  estimatedCost: z.number(),
  source: z.string(),
  referenceId: z.string().nullable(),
  referenceType: z.string().nullable(),
  createdAt: z.date(),
})

export type LlmUsage = z.infer<typeof LlmUsageSchema>

// ============================================
// LLM Usage Input (for creating usage records)
// ============================================

export const LlmUsageInputSchema = z.object({
  modelId: z.string(),
  workplaceId: z.string().optional(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  /** When set, skip token-based cost and store this value */
  estimatedCost: z.number().optional(),
  source: z.string(),
  referenceId: z.string().optional(),
  referenceType: z.string().optional(),
})

export type LlmUsageInput = z.infer<typeof LlmUsageInputSchema>

// ============================================
// LLM Generation Options
// ============================================

export const LlmGenerationOptionsSchema = z.object({
  modelId: z.string().optional(),
  workplaceId: z.string().optional(),
  source: z.string().optional(),
  trackUsage: z.boolean().default(true),
})

export type LlmGenerationOptions = z.infer<typeof LlmGenerationOptionsSchema>

// ============================================
// LLM Generation Result (includes usage info)
// ============================================

export const LlmGenerationResultSchema = z.object({
  text: z.string(),
  usage: z
    .object({
      inputTokens: z.number(),
      outputTokens: z.number(),
      totalTokens: z.number(),
      estimatedCost: z.number(),
    })
    .optional(),
})

export type LlmGenerationResult = z.infer<typeof LlmGenerationResultSchema>

// ============================================
// Default Constants
// ============================================

/**
 * Default model ID for new agents in gateway format (used as fallback)
 * The actual default comes from the database (isDefault = true)
 */
export const DEFAULT_MODEL_ID = 'openai/gpt-5-nano'

/**
 * Default speech-to-text model for audio.transcribe.
 * AssemblyAI Universal-2 with speaker diarization (not a chat LanguageModel).
 */
export const DEFAULT_AUDIO_TRANSCRIPTION_MODEL_ID = 'assemblyai/universal-2'

/**
 * Fast model ID for agent planning/tool selection steps.
 * Uses Gemini 2.5 Flash Lite for speed - optimized for classification and quick tool routing decisions.
 * The agent's configured model is still used for final response generation.
 */
export const FAST_PLANNER_MODEL_ID = 'google/gemini-2.5-flash-lite'

/**
 * Default system prompt for new agents.
 */
export const DEFAULT_SYSTEM_PROMPT = `You are a helpful AI assistant. Your role is to assist users with their requests in a clear, accurate, and friendly manner.

Guidelines:
- Be concise and direct in your responses
- Ask clarifying questions when the user's request is ambiguous
- If you don't know something, say so rather than making up information
- Format your responses clearly using markdown when appropriate
- Be professional yet approachable`

// ============================================
// @deprecated - Legacy types for backwards compatibility
// ============================================

/**
 * @deprecated Use modelId string instead - models are now database-driven
 */
export type SupportedModel = string

/**
 * @deprecated Use modelId string instead - models are now database-driven
 */
export type SupportedOpenAIModel = string

/**
 * @deprecated Use modelId string instead - models are now database-driven
 */
export type SupportedGeminiModel = string

/**
 * @deprecated Use modelId string instead - models are now database-driven
 */
export type StreamableModel = string

/**
 * @deprecated Use modelId string instead - models are now database-driven
 */
export type GenerativeModel = string

/**
 * @deprecated Kept for backwards compatibility - use database-driven model lookup instead
 */
export const DEFAULT_AGENT_MODEL = DEFAULT_MODEL_ID
